import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import { fetchUserStoryById, updateUserStory } from "./userStoryService";
import "../assets/css/EditCustomer.css";
import "../assets/css/Projects.css";
import "../assets/css/UserStories.css";

// Backend validation expects only these 3 values.
const USER_STORY_STATUSES = ["New", "In Progress", "Completed"];
const USER_STORY_PRIORITIES = ["High", "Medium", "Low"];

function EditUserStories({ onBack, onUpdate }) {
  const navigate = useNavigate();
  const { id: projectId, subprojectId, userStoryId } = useParams();
  const hasSubproject = Boolean(subprojectId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [projects, setProjects] = useState([]);
  const [subprojects, setSubprojects] = useState([]);
  const [loadingSubprojects, setLoadingSubprojects] = useState(false);
  const [formData, setFormData] = useState({
    projectId: "",
    subProjectId: "",
    title: "",
    description: "",
    status: "New",
    priority: ""
  });

  // Fetch subprojects when project changes
  useEffect(() => {
    if (!formData.projectId) {
      setSubprojects([]);
      return;
    }
    let cancelled = false;
    setLoadingSubprojects(true);
    axiosInstance
      .get(`/sub-projects/project/${formData.projectId}`)
      .then((res) => {
        if (cancelled) return;
        const raw = res.data;
        let arr = [];
        if (Array.isArray(raw)) arr = raw;
        else if (raw?.sub_projects) arr = raw.sub_projects;
        else if (raw?.data) arr = raw.data;
        const mapped = (arr || [])
          .map((sp) => ({
            id: sp.Sub_Project_Id ?? sp.sub_project_id ?? sp.id,
            name: sp.Sub_Project_Name ?? sp.sub_project_name ?? sp.name ?? ""
          }))
          .filter((x) => x.id != null && String(x.name).trim() !== "");
        setSubprojects(mapped);
      })
      .catch(() => {
        if (!cancelled) setSubprojects([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSubprojects(false);
      });
    return () => { cancelled = true; };
  }, [formData.projectId]);

  useEffect(() => {
    const load = async () => {
      if (!userStoryId) return;
      setLoading(true);
      setError("");
      try {
        const [story, projectsRes] = await Promise.all([
          fetchUserStoryById(userStoryId),
          axiosInstance.get("/projects").catch(() => ({ data: [] }))
        ]);
        if (Array.isArray(projectsRes?.data)) {
          setProjects(projectsRes.data);
        } else if (projectsRes?.data?.data) {
          setProjects(projectsRes.data.data);
        }
        if (story) {
          const rawStatus = story.Status || story.status || "";
          const normalizedStatus = rawStatus === "Not Started"
            ? "New"
            : (USER_STORY_STATUSES.includes(rawStatus) ? rawStatus : "New");
          const storyProjectId = story.Project_Id ?? story.project_id ?? projectId ?? "";
          const storySubProjectId = story.Sub_Project_Id ?? story.sub_project_id ?? subprojectId ?? "";
          setFormData({
            projectId: storyProjectId ? String(storyProjectId) : "",
            subProjectId: storySubProjectId ? String(storySubProjectId) : "",
            title: story.Title || story.title || "",
            description: story.Description || story.description || "",
            status: normalizedStatus,
            priority: story.Priority || story.priority || ""
          });
        } else {
          setError("User story not found.");
        }
      } catch (err) {
        console.error("Error fetching user story:", err);
        setError(err.response?.data?.message || "Failed to load user story.");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStoryId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const title = (formData.title || "").trim();
    if (!title) {
      alert("Title is required.");
      return;
    }
    let updatedBy = 1;
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        updatedBy = user.user_id || user.id || 1;
      }
    } catch (err) {
      console.error("Error reading user:", err);
    }
    const effectiveProjectId = formData.projectId ? Number(formData.projectId) : null;
    if (!effectiveProjectId || !Number.isFinite(effectiveProjectId)) {
      alert("Please select a project.");
      return;
    }
    const uiStatus = USER_STORY_STATUSES.includes(formData.status) ? formData.status : "New";
    const backendStatus = uiStatus === "New" ? "Not Started" : uiStatus;
    const payload = {
      Project_Id: effectiveProjectId,
      Title: title,
      Description: (formData.description || "").trim(),
      Status: backendStatus,
      Updated_By: updatedBy
    };
    if (formData.subProjectId) {
      payload.Sub_Project_Id = Number(formData.subProjectId);
    } else {
      payload.Sub_Project_Id = null;
    }
    if (formData.priority) {
      payload.Priority = formData.priority;
    }
    setSubmitting(true);
    try {
      await updateUserStory(userStoryId, payload);
      alert("User story updated successfully.");

      if (onUpdate) onUpdate();

      if (onBack) onBack();
      else if (payload.Sub_Project_Id) {
        navigate(`/projects/${effectiveProjectId}/subprojects/${payload.Sub_Project_Id}/view`);
      } else {
        navigate(`/projects/${effectiveProjectId}/view`);
      }
    } catch (err) {
      console.error("Error updating user story:", err);
      alert(err.response?.data?.message || err.response?.data?.error || "Failed to update user story.");
    } finally {
      setSubmitting(false);
    }
  };

  const goBack = () => {
    if (onBack) onBack();
    else if (hasSubproject) {
      navigate(`/projects/${projectId}/subprojects/${subprojectId}/view`);
    } else {
      navigate(`/projects/${projectId}/view`);
    }
  };

  if (loading) {
    return (
      <div className="project-form-container editcustomer-container">
        <div className="editcustomer-loading">Loading user story...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="project-form-container editcustomer-container">
        <button type="button" className="viewlead-back-link" onClick={goBack}>
          <FaArrowLeft /> Back
        </button>
        <div className="error-message" style={{ marginTop: 16 }}>{error}</div>
      </div>
    );
  }

  return (
    <div className="project-form-container editcustomer-container">
      <div className="viewlead-header">
        <button type="button" className="viewlead-back-link" onClick={goBack}>
          <FaArrowLeft /> Back
        </button>
      </div>
      <form className="editcustomer-form edituserstory-form" onSubmit={handleSubmit}>
        <div className="form-section">
          <div className="form-grid">
            <div className="form-group">
              <label>Project <span className="required">*</span></label>
              <select
                name="projectId"
                value={formData.projectId}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData((prev) => ({ ...prev, projectId: val, subProjectId: "" }));
                }}
                required
              >
                <option value="">Select Project</option>
                {(projects || []).map((p) => {
                  const id = p.Project_Id ?? p.project_id ?? p.id;
                  const name = p.Project_Name ?? p.project_name ?? p.name ?? "";
                  return (
                    <option key={id} value={String(id)}>
                      {name || `Project ${id}`}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="form-group">
              <label>Subproject</label>
              <select
                name="subProjectId"
                value={formData.subProjectId}
                onChange={handleInputChange}
                disabled={loadingSubprojects || !formData.projectId}
              >
                <option value="">None (project-level)</option>
                {(subprojects || []).map((sp) => (
                  <option key={sp.id} value={String(sp.id)}>
                    {sp.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Title <span className="required">*</span></label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
               
              />
            </div>
            <div className="form-group">
              <label>Status <span className="required">*</span></label>
              <select name="status" value={formData.status} onChange={handleInputChange} required>
                {USER_STORY_STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Priority</label>
              <select name="priority" value={formData.priority} onChange={handleInputChange}>
                <option value="">Select Priority</option>
                {USER_STORY_PRIORITIES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="4"
               
              />
            </div>
          </div>
        </div>
        <div className="form-actions editcustomer-actions">
          <button type="submit" className="btn-subproject-primary" disabled={submitting}>
            {submitting ? "Updating..." : "Update User Story"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditUserStories;
